/**
 * Handles Cognito Hosted UI redirect: ?code=&state= → tokens → app session.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@stepflow/ui-shell/context/AuthContext.jsx';
import {
  exchangeAuthorizationCodeForTokens,
  clearOAuthSessionStorage,
  getStoredOAuthState,
} from '@/services/cognitoOAuth';
import { establishSessionFromOAuthTokens } from '@/services/cognitoService';
import {
  createStripeSetupSession,
  isStripeCheckoutEnabled,
  STRIPE_SETUP_PENDING_KEY,
} from '@/services/stripeBilling';
import { getPendingInviteToken } from '@/services/workspaceApi';
import { canAccessNetworkWorkbench } from '@/utils/stepflowRoles';
import { APP_BROWSE_DEFAULT_HOME } from '@stepflow/ui-shared/constants/appBrowseFlags.js';

// Post-login landing for network-workbench roles: the Apps browse home when the
// flag is on, else the classic create flow. USER role always lands on /agentic.
const workbenchLanding = () => (APP_BROWSE_DEFAULT_HOME ? '/registry' : '/network/new');

const OAuthCallbackPage = () => {
  const [message, setMessage] = useState('Signing you in…');
  const [failed, setFailed] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { finalizeOAuthSignIn } = useAuth();
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    let cancelled = false;

    async function run() {
      const errParam = params.get('error');
      const errDesc = params.get('error_description');
      if (errParam) {
        clearOAuthSessionStorage();
        setFailed(true);
        setMessage(decodeURIComponent(errDesc || errParam || 'Sign-in was cancelled.'));
        return;
      }

      const code = params.get('code');
      const state = params.get('state');
      if (!code) {
        clearOAuthSessionStorage();
        setFailed(true);
        setMessage('Missing authorization code.');
        return;
      }

      const expected = getStoredOAuthState();
      if (!state || !expected || state !== expected) {
        clearOAuthSessionStorage();
        setFailed(true);
        setMessage('Invalid sign-in state. Please try again from the login page.');
        return;
      }

      try {
        const tokens = await exchangeAuthorizationCodeForTokens(code);
        await establishSessionFromOAuthTokens(tokens);
        const result = await finalizeOAuthSignIn();
        if (cancelled) return;
        if (!result.success) {
          setFailed(true);
          setMessage(result.error || 'Could not complete sign-in.');
          return;
        }

        const pendingStripe = localStorage.getItem(STRIPE_SETUP_PENDING_KEY) === '1';
        if (pendingStripe && isStripeCheckoutEnabled()) {
          localStorage.removeItem(STRIPE_SETUP_PENDING_KEY);
          try {
            const checkoutUrl = await createStripeSetupSession({
              successPath: canAccessNetworkWorkbench(result.user?.stepflowRole)
                ? workbenchLanding()
                : '/agentic',
            });
            window.location.assign(checkoutUrl);
            return;
          } catch {
            navigate('/pricing', { replace: true });
            return;
          }
        }

        const pendingInvite = getPendingInviteToken();
        if (pendingInvite) {
          navigate(`/join?token=${encodeURIComponent(pendingInvite)}`, { replace: true });
          return;
        }

        if (result.hasActiveSubscription) {
          navigate(
            canAccessNetworkWorkbench(result.user?.stepflowRole) ? workbenchLanding() : '/agentic',
            { replace: true },
          );
        } else {
          navigate('/pricing', { replace: true });
        }
      } catch (e) {
        clearOAuthSessionStorage();
        if (!cancelled) {
          setFailed(true);
          setMessage(e.message || 'Sign-in failed.');
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [params, navigate, finalizeOAuthSignIn]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 px-6">
      <p className={`text-center text-base max-w-md ${failed ? 'text-red-600' : 'text-gray-700'}`}>{message}</p>
      {failed && (
        <Link to="/login" className="text-blue-600 hover:underline text-sm font-medium">
          Back to login
        </Link>
      )}
    </div>
  );
};

export default OAuthCallbackPage;
